import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgxPaginationModule, PaginationInstance } from 'ngx-pagination';
import { combineLatest, merge, Observable, of, Subject, Subscription } from 'rxjs';
import {
    distinctUntilChanged,
    map,
    mapTo,
    share,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { faCheck, faFilter, faSadCry } from '@fortawesome/free-solid-svg-icons';
import {
    ArticleResultFragment,
    GetCollectionDocument,
    GetCollectionQuery,
    GetSearchFacetValuesDocument,
    LogSearchListViewedDocument,
    LogSearchResultClickDocument,
    SearchProductsQuery,
    SearchProductsQueryVariables,
    SortOrder,
} from '../../common/gql/graphql';
import { HasMetadata, ProductCardItem } from '../../../common/types';
import { getRouteArrayParam } from '../../../common/utils/get-route-array-param';
import { toProductCardItem } from '../../../common/utils/to-product-card-item';
import { DataService } from '../../../core/providers/data/data.service';
import { StateService } from '../../../core/providers/state.service';
import { FacetFilterService, FacetValueIdPair } from '../../providers/facet-filter.service';
import { IsrService } from '../../../core/providers/isr/isr-service';
import { SortByPositionPipe } from '../../pipes/sort-by-position.pipe';
import { CanonicalCollectionSlugPipe } from '../../../shared/pipes/canonical-collection-slug.pipe';
import { ProductListFiltersComponent } from '../product-list-filters/product-list-filters.component';
import { SideTrayComponent } from '../../../shared/components/side-tray/side-tray.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { ProductCardComponent } from '../../../shared/components/product-card/product-card.component';
import { ComboBoxLabelDirective } from '../../../shared/components/combo-box/combo-box-label.directive';
import { ComboBoxComponent } from '../../../shared/components/combo-box/combo-box.component';
import { ActiveFiltersComponent } from '../active-filters/active-filters.component';
import { ProductListFiltersHorizontalComponent } from '../product-list-filters-horizontal/product-list-filters-horizontal.component';
import { FilterPresetLinkComponent } from '../filter-preset-link/filter-preset-link.component';
import { TabbedDescriptionComponent } from '../tabbed-description/tabbed-description.component';
import { PictureComponent } from '../../../shared/components/picture/picture.component';
import { CollectionBreadcrumbsComponent } from '../collection-breadcrumbs/collection-breadcrumbs.component';
import { SEARCH_PRODUCTS } from './product-list.graphql';

export type ProductListSort = 'default' | 'p_lh' | 'p_hl';

@Component({
    selector: 'vsf-product-list',
    templateUrl: './product-list.component.html',
    styleUrls: ['./product-list.component.scss'],
    providers: [FacetFilterService],
    standalone: true,
    imports: [
        CollectionBreadcrumbsComponent,
        NgIf,
        NgFor,
        RouterLink,
        PictureComponent,
        TabbedDescriptionComponent,
        FilterPresetLinkComponent,
        ProductListFiltersHorizontalComponent,
        FontAwesomeModule,
        ActiveFiltersComponent,
        ComboBoxComponent,
        ComboBoxLabelDirective,
        ProductCardComponent,
        NgClass,
        PaginationComponent,
        SideTrayComponent,
        ProductListFiltersComponent,
        AsyncPipe,
        NgxPaginationModule,
        CanonicalCollectionSlugPipe,
        SortByPositionPipe,
    ],
})
export class ProductListComponent implements OnInit, OnDestroy, HasMetadata {
    products$: Observable<ProductCardItem[]>;
    articles$: Observable<
        Array<
            ArticleResultFragment & {
                highlightedTitle: string;
            }
        >
    >;
    currentPage$: Observable<number>;
    totalResults$: Observable<number>;
    sortOrder$: Observable<ProductListSort>;
    paginationConfig$: Observable<PaginationInstance>;
    collection$: Observable<GetCollectionQuery['collection'] | null | undefined>;
    subCollectionsNonExcluded$: Observable<GetCollectionQuery['collection']['children']>;
    subCollectionsExcluded$: Observable<GetCollectionQuery['collection']['children']>;
    extendedDescription$: Observable<string | undefined>;
    layoutMode$: Observable<'default' | 'fullWidth'>;
    searchFilterCollection$: Observable<GetCollectionQuery['collection'] | null | undefined>;
    searchTopCollections$: Observable<SearchProductsQuery['search']['topCollections'] | undefined>;
    featuredFacetIds$: Observable<string[]>;
    excludedFacetIds$: Observable<string[]>;
    isLeafCollection$: Observable<boolean>;
    loading$: Observable<boolean>;
    priceMinMax$: Observable<{ min?: number; max?: number } | undefined>;
    searchTerm$: Observable<string>;
    isSearchPage$: Observable<boolean>;
    searchQueryId = '';
    mobileFiltersExpanded = false;
    private init$ = new Subject<void>();
    private subscription: Subscription;
    readonly placeholderProducts = Array.from({ length: 12 }).map(() => null);
    readonly sortOrders: Array<{ label: string; order: ProductListSort }> = [
        { label: 'Default', order: 'default' },
        { label: 'Price: low to high', order: 'p_lh' },
        { label: 'Price: high to low', order: 'p_hl' },
    ];
    filter = faFilter;
    check = faCheck;
    sadCry = faSadCry;

    constructor(
        private dataService: DataService,
        private route: ActivatedRoute,
        private router: Router,
        private stateService: StateService,
        public facetFilterService: FacetFilterService,
        private isrService: IsrService,
    ) {}

    pageMetadata = this.init$.pipe(
        switchMap(() => this.collection$),
        map((collection) => {
            if (collection) {
                const imagePreview =
                    collection.customFields?.seoImage?.preview ?? collection.featuredAsset?.preview;
                const title = collection.customFields?.seoTitle ?? `Buy ${collection.name} Online`;
                const description =
                    collection.customFields?.seoDescription ??
                    `Shop our wide range of ${collection.name} online at Bromleys Art Supplies. Price Match Promise. Fast Delivery. Great Customer Service.`;
                return {
                    title,
                    description,
                    image: imagePreview ? `${imagePreview}?preset=large` : undefined,
                    noIndex: collection.customFields?.noIndex === true,
                    canonicalUrl: `/c/${collection.slug}`,
                };
            }
            return undefined;
        }),
    );

    ngOnInit() {
        const perPage = 24;
        this.subscription = this.route.queryParamMap
            .pipe(
                map((pm) => getRouteArrayParam(pm, 'facets').filter((id) => /^\d+-\d+$/.test(id))),
                distinctUntilChanged((x, y) => x.toString() === y.toString()),
            )
            .subscribe((ids) => {
                this.facetFilterService.setActiveFacetValueIds(ids as FacetValueIdPair[]);
            });
        const collectionSlug$ = combineLatest(
            this.route.paramMap.pipe(map((pm) => pm.get('slug'))),
            this.route.queryParamMap.pipe(map((qpm) => qpm.get('slug'))),
        ).pipe(
            map(([routeSlug, querySlug]) => routeSlug || querySlug),
            distinctUntilChanged(),
            tap((slug) => {
                this.stateService.setState('lastCollectionSlug', slug || null);
            }),
            shareReplay(1),
        );
        this.isSearchPage$ = this.route.data.pipe(map((data) => data.isSearchPage));

        this.searchTerm$ = this.route.queryParamMap.pipe(
            map((pm) => pm.get('term') || ''),
            distinctUntilChanged(),
            shareReplay(1),
            tap(() => this.facetFilterService.init()),
        );
        this.priceMinMax$ = this.route.queryParamMap.pipe(
            map((pm) => {
                const result: { min?: number; max?: number } = {};
                const min = pm.get('min');
                const max = pm.get('max');
                if (min != null) {
                    result.min = +min;
                }
                if (max != null) {
                    result.max = +max;
                }
                return result;
            }),
            distinctUntilChanged(),
            shareReplay(1),
        );
        this.sortOrder$ = this.route.queryParamMap.pipe(
            map((pm) => (pm.get('sort') || 'default') as ProductListSort),
            distinctUntilChanged(),
            shareReplay(1),
        );

        this.currentPage$ = this.route.queryParamMap.pipe(map((pm) => +(pm.get('p') || 1)));

        const triggerFetch$ = combineLatest(
            this.currentPage$,
            collectionSlug$,
            this.facetFilterService.groupedActiveFacetValueIds$,
            this.searchTerm$,
            this.priceMinMax$,
            this.isSearchPage$,
            this.sortOrder$,
        ).pipe(share());

        const queryResult$ = collectionSlug$.pipe(
            withLatestFrom(
                this.facetFilterService.groupedActiveFacetValueIds$,
                this.searchTerm$,
                this.priceMinMax$,
                this.sortOrder$,
            ),
            tap(([collectionSlug, facetValueIds, term, priceRange, sortOrder]) => {
                this.facetFilterService.init();
                if (0 < facetValueIds.length || priceRange?.min || priceRange?.max) {
                    // On page load, there is at least 1 FacetValue filter enabled, so we want to
                    // first fetch _all_ results without facetValues
                    this.dataService
                        .query(
                            GetSearchFacetValuesDocument,
                            this.buildSearchInput({ collectionSlug, term, facetValueIds: [] }),
                        )
                        .pipe(take(1))
                        .subscribe(({ search }) => {
                            this.facetFilterService.setFacetValues(search.totalItems, search.facetValues);
                        });
                }
            }),
            switchMap(() => triggerFetch$),
            switchMap(
                ([currentPage, collectionSlug, facetValueIds, term, priceRange, isSearchPage, sortOrder]) => {
                    return combineLatest([
                        this.dataService
                            .query(
                                SEARCH_PRODUCTS,
                                this.buildSearchInput({
                                    collectionSlug,
                                    term,
                                    facetValueIds,
                                    priceRange,
                                    currentPage,
                                    perPage,
                                    logAnalytics: isSearchPage,
                                    sortOrder,
                                }),
                                { ssr: true },
                            )
                            .pipe(
                                tap((data) => {
                                    this.searchQueryId = data.search.queryId ?? '';
                                    this.facetFilterService.setFacetValues(
                                        data.search.totalItems,
                                        data.search.facetValues,
                                    );
                                }),
                            ),
                        of(collectionSlug).pipe(
                            switchMap((slug) => {
                                if (slug) {
                                    return this.dataService
                                        .query(
                                            GetCollectionDocument,
                                            {
                                                slug,
                                            },
                                            { ssr: true },
                                        )
                                        .pipe(map((data) => data.collection));
                                } else {
                                    return of(null);
                                }
                            }),
                        ),
                    ]);
                },
            ),
            shareReplay(1),
        );

        this.loading$ = merge(triggerFetch$.pipe(mapTo(true)), queryResult$.pipe(mapTo(false))).pipe(
            startWith(true),
        );

        const searchResult$ = queryResult$.pipe(map((result) => result[0].search));
        const activeCollection$ = queryResult$.pipe(map((result) => result[1]));
        this.collection$ = combineLatest(this.isSearchPage$, activeCollection$).pipe(
            map(([isSearchPage, collection]) => {
                if (!isSearchPage && !collection) {
                    this.isrService.doNotCache();
                    this.router.navigate(['/c/404/', this.route.snapshot.paramMap.get('slug')]);
                }
                if (this.route.snapshot.paramMap.get('slug') === 'brands' && collection) {
                    const children =
                        collection.children?.slice(0).sort((a, b) => (a.name < b.name ? -1 : 1)) ?? [];
                    return { ...collection, children };
                }
                return isSearchPage ? null : collection;
            }),
            shareReplay(1),
        );
        this.subCollectionsNonExcluded$ = this.collection$.pipe(
            map(
                (collection) =>
                    collection?.children?.filter((c) => c.customFields.excludeFromSubCollections !== true) ??
                    [],
            ),
        );
        this.subCollectionsExcluded$ = this.collection$.pipe(
            map(
                (collection) =>
                    collection?.children?.filter((c) => c.customFields.excludeFromSubCollections === true) ??
                    [],
            ),
        );
        this.extendedDescription$ = this.collection$.pipe(
            map((collection) => collection?.customFields?.extendedDescription),
        );
        this.layoutMode$ = this.collection$.pipe(
            map((collection) => (collection?.customFields?.layout === 'fullWidth' ? 'fullWidth' : 'default')),
            startWith<'default' | 'fullWidth'>(
                this.route.snapshot.data['isSearchPage'] ? ('default' as const) : ('fullWidth' as const),
            ),
        );

        this.searchFilterCollection$ = combineLatest(this.isSearchPage$, activeCollection$).pipe(
            map(([isSearchPage, collection]) => (isSearchPage ? collection : null)),
        );

        this.featuredFacetIds$ = this.collection$.pipe(
            map((collection) => collection?.customFields?.featuredFacets?.map((f) => f.id) ?? []),
        );
        this.excludedFacetIds$ = this.collection$.pipe(
            map((collection) => collection?.customFields?.excludedFacets?.map((f) => f.id) ?? []),
        );
        this.isLeafCollection$ = this.collection$.pipe(map((collection) => !collection?.children?.length));
        this.products$ = searchResult$.pipe(
            map((search) => search.items),
            map((items) => items.map(toProductCardItem)),
        );
        this.articles$ = queryResult$.pipe(
            map((result) => {
                const externalResults = result[0].searchExternal[0];
                if (externalResults.__typename === 'CmsArticlesResponse') {
                    return (externalResults.items || [])
                        .filter((item) => !!item.highlights.find((h) => h.field === 'title'))
                        .map((item) => ({
                            ...item,
                            highlightedTitle:
                                item.highlights.find((h) => h.field === 'title')?.snippet ?? item.title,
                        }));
                } else {
                    return [];
                }
            }),
        );
        this.searchTopCollections$ = combineLatest(this.isSearchPage$, searchResult$).pipe(
            map(([isSearchPage, search]) => (isSearchPage ? search.topCollections.slice(0, 5) : undefined)),
        );
        this.totalResults$ = searchResult$.pipe(map((search) => search.totalItems));
        this.paginationConfig$ = combineLatest(this.totalResults$, this.currentPage$).pipe(
            map(([totalItems, currentPage]) => ({ currentPage, totalItems, itemsPerPage: perPage })),
        );

        this.subscription.add(
            queryResult$.pipe(withLatestFrom(this.isSearchPage$)).subscribe(([data, isSearchPage]) => {
                if (isSearchPage) {
                    this.logListView();
                }
            }),
        );
        this.init$.next(undefined);
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    trackByProductId(index: number, item: ProductCardItem) {
        return item.productId;
    }

    getSortOrderLabel(order: ProductListSort): string {
        return this.sortOrders.find((i) => i.order === order)?.label ?? '';
    }

    setCurrentPage(page: number) {
        this.router.navigate(['./'], {
            queryParams: { p: page },
            relativeTo: this.route,
            queryParamsHandling: 'merge',
        });
    }

    setSortOrder(order: ProductListSort) {
        this.router.navigate(['./'], {
            queryParams: { sort: order },
            relativeTo: this.route,
            queryParamsHandling: 'merge',
        });
    }

    logResultClick(resultId: string, position: number) {
        if (this.searchQueryId && resultId) {
            this.dataService
                .mutate(LogSearchResultClickDocument, {
                    input: {
                        queryId: this.searchQueryId,
                        resultId,
                        position,
                    },
                })
                .subscribe();
        }
    }

    private buildSearchInput(options: {
        collectionSlug: string | null;
        term: string;
        facetValueIds: string[][];
        priceRange?: { min?: number; max?: number };
        currentPage?: number;
        perPage?: number;
        logAnalytics?: boolean;
        sortOrder?: ProductListSort;
    }): SearchProductsQueryVariables {
        const {
            collectionSlug,
            term,
            facetValueIds,
            priceRange,
            currentPage,
            perPage,
            logAnalytics,
            sortOrder,
        } = options;
        const searchInput: SearchProductsQueryVariables = {
            input: {
                groupByProduct: true,
                collectionSlug: collectionSlug ?? undefined,
                term,
                facetValueFilters: facetValueIds.map((group) =>
                    group.length === 1 ? { and: group[0] } : { or: group },
                ),
                take: perPage,
                skip: ((currentPage ?? 1) - 1) * (perPage ?? 10),
                priceRangeWithTax: {
                    min: priceRange?.min ?? 0,
                    max: priceRange?.max ?? 999999900,
                },
                logAnalytics,
                topCollectionsFromTop: term ? 10 : 0,
            },
            inputExternal: {
                term,
                take: 10,
                indexes: ['cms-articles'],
            },
        };
        if (sortOrder === 'p_lh') {
            searchInput.input.sort = {
                price: SortOrder.ASC,
            };
        }
        if (sortOrder === 'p_hl') {
            searchInput.input.sort = {
                price: SortOrder.DESC,
            };
        }
        return searchInput;
    }

    private logListView() {
        if (this.searchQueryId) {
            this.dataService
                .mutate(LogSearchListViewedDocument, {
                    input: {
                        queryId: this.searchQueryId,
                    },
                })
                .subscribe();
        }
    }
}
